/**
 * CR-7781: Customer Changes Haulage Cost. CR-8613: widened to every haulage
 * detail and the expected pickup date.
 *
 * Toggles the `call_off_customer_haulage_cost` feature flag. While it is on,
 * this tenant's users may change the expected pickup date and the haulier's
 * details — haulage cost, pickup date, agreed delivery date and lorry number —
 * on any call-off that was not cancelled; the haulage details are otherwise
 * the haulier's alone, and the expected pickup date is otherwise changed only
 * through Edit Call-Off, which closes at Dispatched.
 * Every change needs a reason, and both are kept in the call-off's audit
 * history. The flag keeps its CR-7781 name so tenants that switched it on keep
 * it on.
 *
 * Lives in Admin Setup → Call-Off ({@link CallOffConfigTab}). Per customer
 * only: the server reads it from the customer's own settings, so the section
 * is not shown to Helix users, whose Admin Setup saves the platform-wide row —
 * for them it would be a switch that does nothing.
 *
 * Same round-trip as {@link ProdumaxExportSection}: the whole feature_flags
 * dict is saved so sibling flags are preserved.
 */
import { useState, useEffect } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { AlertCircle, Loader2, Truck } from 'lucide-react';
import { toast } from 'sonner';

const FLAG = 'call_off_customer_haulage_cost';

export function CustomerHaulageCostSection() {
  const { user } = useAuth();
  const isHelixUser = user?.platform === 'HELIX' || user?.company_type === 'helix';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  // A failed load must not read as "switched off".
  const [loadError, setLoadError] = useState<string | null>(null);
  // The full feature_flags dict is kept so a toggle save doesn't drop siblings.
  const [featureFlags, setFeatureFlags] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (isHelixUser) return;
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        const flags = data.feature_flags ?? {};
        setFeatureFlags(flags);
        setEnabled(!!flags[FLAG]);
      } catch (err: unknown) {
        console.error('Error loading the haulage cost setting:', err);
        setLoadError('The setting could not be loaded. Refresh the page to try again.');
        toast.error('Failed to load the haulage cost setting');
      } finally {
        setLoading(false);
      }
    })();
  }, [isHelixUser]);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    setSaving(true);
    try {
      const nextFlags = { ...featureFlags, [FLAG]: checked };
      await platformSettingsAPI.patchSettings({ feature_flags: nextFlags });
      setFeatureFlags(nextFlags);
      toast.success(`Customer changes pickup and haulage details: ${checked ? 'ON' : 'OFF'}`);
    } catch (err: unknown) {
      console.error('Error saving the haulage cost setting:', err);
      toast.error('Failed to save the haulage cost setting');
      setEnabled(previous);
    } finally {
      setSaving(false);
    }
  };

  if (isHelixUser) return null;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5" />
          Customer Changes Pickup and Haulage Details
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Lets your users change the expected pickup date and the haulier&apos;s details
          (haulage cost, pickup date, agreed delivery date and lorry number) on a call-off,
          at any stage until it is cancelled. Every change needs a reason, and the change and
          its reason are recorded in the call-off&apos;s audit history.
        </p>
      </CardHeader>
      <CardContent>
        {loadError ? (
          <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {loadError}
          </p>
        ) : (
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="customer-haulage-cost-flag" className="text-sm font-medium">
              Allow users to change the pickup and haulage details
            </Label>
            <p className="text-xs text-muted-foreground">
              {enabled
                ? 'ON — your users can change the pickup and haulage details, giving a reason each time.'
                : 'OFF — only the haulier enters the haulage details, and the expected pickup date changes through Edit Call-Off only.'}
            </p>
          </div>
          <Switch
            id="customer-haulage-cost-flag"
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
          />
        </div>
        )}
      </CardContent>
    </Card>
  );
}
